/**
 * Service for truck status-specific operations
 * Provides methods for status-related truck operations
 * Standardized to match database schema
 */

import { isBackwardMovement, determineCurrentStatus } from '../pages/tiles/truck_scheduler/components/utils/statusUtils';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * General function to update truck status
 * Supports both forward and backward status changes
 * 
 * @param {string} mode - 'inbound' or 'outbound'
 * @param {number} truckId - The ID of the truck to update
 * @param {string} status - The new status to set
 * @param {Object} options - Additional options for the update
 * @param {Object} options.timestamps - Timestamp fields to update
 * @param {string} options.direction - Direction of status change ('forward' or 'backward')
 * @param {string} options.fromStatus - Current status (required for backward movement)
 * @param {Object} options.truck - Complete truck object for local updates
 * @returns {Promise} - API response with updated truck data
 */
export const updateTruckStatus = async (mode, truckId, status, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Map mode to table name
    const table = mode === 'inbound' ? 'inbound-trucks' : 'outbound-trucks';
    
    // Extract options
    const { timestamps = {}, direction, fromStatus, truck = null } = options;
    
    // Determine if this is a backward movement
    const isBackward = direction === 'backward';
    
    // Build the request payload
    const payload = {
      status,
      timestamps,
      direction: isBackward ? 'backward' : 'forward'
    };
    
    // Add fromStatus for backward movement
    if (isBackward) {
      if (!fromStatus) {
        throw new Error('fromStatus is required for backward status movement');
      }

      // Preserve original case for fromStatus to ensure server validation works correctly
      payload.fromStatus = fromStatus;
      console.log(`truckStatusService: Moving ${mode} truck #${truckId} backward from ${payload.fromStatus} to ${status}`);
    } else {
      console.log(`truckStatusService: Updating ${mode} truck #${truckId} status to ${status}`);
    }
    
    // If we have the complete truck object, use standardized approach for local changes
    if (truck) {
      // Generate standard status changes locally - implement our own status standardization
      const updatedTruck = { ...truck, status };

      if (!isBackward) {
        // For forward status changes, add a timestamp for the new status
        if (mode === 'inbound') {
          if (status === 'Arrived') updatedTruck.arrival_datetime = new Date().toISOString();
          else if (status === 'Received') updatedTruck.started_at = new Date().toISOString();
          else if (status === 'Putaway') updatedTruck.putaway_at = new Date().toISOString();
          else if (status === 'Completed') updatedTruck.completed_at = new Date().toISOString();
        } else { // outbound
          if (status === 'Staged') updatedTruck.staging_start = new Date().toISOString();
          else if (status === 'Loaded') updatedTruck.loading_complete = new Date().toISOString();
          else if (status === 'Shipped') updatedTruck.departure_datetime = new Date().toISOString();
          else if (status === 'Completed') updatedTruck.completed_at = new Date().toISOString();
        }
      }
      console.log('truckStatusService: Generated standardized updates:', 
        Object.entries(updatedTruck)
          .filter(([key, value]) => truck[key] !== value)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      );
      
      // Add these to the payload - only the changes
      payload.standardized_changes = Object.entries(updatedTruck)
        .filter(([key, value]) => truck[key] !== value)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    }
    
    // Make the API request
    const response = await fetch(`${API_URL}/status/${table}/${truckId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = `Failed to update truck status: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Ignore JSON parsing errors
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in updateTruckStatus:', error);
    throw error;
  }
};

/**
 * Move a truck backward in status
 * 
 * @param {string} mode - 'inbound' or 'outbound'
 * @param {number} truckId - The ID of the truck to move
 * @param {string} targetStatus - Target status (e.g., 'Arrived')
 * @param {Object} truck - Complete truck object for standardized updates
 * @param {Date|string|null} customTimestamp - Optional custom timestamp for the target status
 * @returns {Promise} - API response with updated truck data
 */
export const moveTruckBackward = async (mode, truckId, targetStatus, truck, customTimestamp = null) => {
  try {
    console.log(`truckStatusService: Moving ${mode} truck #${truckId} to status ${targetStatus}`);
    
    // Validate truck object is provided
    if (!truck) {
      throw new Error('Truck object is required for standardized status movement');
    }
    
    // Trust the caller's determination that this is a backward movement
    // Log the movement for debugging
    console.log(`truckStatusService: Processing backward movement for ${mode} truck #${truckId} from ${determineCurrentStatus(truck, mode)} to ${targetStatus}`);

    // No longer verify this is a backward movement - if the caller says it is, trust it
    
    // Build timestamp object if we have a custom timestamp
    const timestamps = {};
    if (customTimestamp) {
      // Standardized timestamp mapping based on schema
      const timestampMap = {
        'inbound': {
          'scheduled': 'scheduled_at',
          'arrived': 'arrived_at',
          'received': 'received_at',
          'putaway': 'putaway_at',
          'completed': 'completed_at'
        },
        'outbound': {
          'scheduled': 'scheduled_at',
          'staged': 'staged_at',
          'loaded': 'loaded_at',
          'shipped': 'shipped_at',
          'completed': 'completed_at'
        }
      };
      
      const fieldMap = timestampMap[mode] || {};
      const timestampField = fieldMap[targetStatus.toLowerCase()];
      
      if (timestampField) {
        timestamps[timestampField] = customTimestamp;
        
        // Also update the truck object with this timestamp
        truck[timestampField] = customTimestamp;
      }
    }
    
    // Try to use the truck's status field directly if available, which should be more accurate
    // than determining it from timestamps
    const fromStatus = truck.status || determineCurrentStatus(truck, mode);

    // Force the current status to ensure consistency
    truck.status = fromStatus;

    // Use the unified endpoint with standardized approach
    return updateTruckStatus(mode, truckId, targetStatus, {
      direction: 'backward',
      fromStatus: fromStatus, // Use explicitly provided status
      timestamps,
      truck
    });
  } catch (error) {
    console.error('Error in moveTruckBackward:', error);
    throw error;
  }
};

export default {
  updateTruckStatus,
  moveTruckBackward
};